import React, { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import CreatableSelect from 'react-select/creatable';
// Types
import { GET_ALL_HASHTAGS } from 'api/hashtags/queries';
// Ui
import { InputFooter } from 'ui3/InputFooter/InputFooter';
// Styles
import styles from './TagsInput.module.scss';

const MAX_TAGS = 5;

type TagsInputProps = {
  editable?: boolean;
  tags: string[];
  label: string;
  labelClassName?: string;
  name: string;
  notice?: string;
  showCounter?: boolean;
  onChange: (value: string[]) => void;
  // `onChange` here yields the tag list, not a DOM event.
} & Omit<React.ComponentPropsWithRef<'input'>, 'onChange'>;

export const TagsInput = ({
  tags = [],
  label,
  editable = true,
  name,
  labelClassName,
  notice = 'Add up to 5 tags.',
  showCounter = true,
  onChange,
}: TagsInputProps) => {
  const [options, setOptions] = useState([]);

  // Format tags to the required structure for react-select
  const formattedTags = (tags || []).map((tag) => ({
    label: tag,
    value: tag,
  }));

  const { refetch } = useQuery(GET_ALL_HASHTAGS, {
    variables: { input: { keyword: '' } },
    skip: true,
  });

  // Debounce function to prevent rapid API calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadOptions = useCallback(
    debounce((inputValue, callback) => {
      if (inputValue) {
        refetch({ input: { keyword: inputValue } })
          .then(({ data }) => {
            const options =
              data?.getAllHashtags?.entities?.map((tag) => ({
                label: tag.name,
                value: tag.name,
              })) || [];
            setOptions(options);
            callback(options);
          })
          .catch((error) => {
            console.error('Error fetching hashtags:', error);
            setOptions([]);
            callback([]);
          });
      } else {
        setOptions([]);
        callback([]);
      }
    }, 500),
    [refetch]
  );

  const handleMenuOpen = () => {
    debouncedLoadOptions('a', (options) => setOptions(options));
  };

  const handleSelectChange = (selectedOptions) => {
    const newTags = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    onChange(newTags);
  };

  const handleCreateOption = (inputValue: string) => {
    const newOption = { label: inputValue, value: inputValue };
    const newOptions = [...options, newOption];
    setOptions(newOptions as []);
    handleSelectChange([...formattedTags, newOption]);
  };

  const handleInputChange = (newValue) => {
    debouncedLoadOptions(newValue, (options) => setOptions(options));
  };

  const currentTagCount = tags?.length || 0;
  const hasError = currentTagCount > MAX_TAGS;
  const errorMessage = hasError ? 'Please add up to 5 tags' : '';
  const noticeMessage = !hasError ? notice : '';

  // Remove dropdown indicator (arrow down) and separator (vertical line)
  const customComponents = {
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
  };

  // staging is showing white background on the menu while local works just as intended,
  // so customStyles is added to try and fix the issue
  const customStyles = {
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#141519',
      color: '#ffffff',
    }),
  };

  const tooltipId = `tags-input-${name}`;

  return (
    <div className={styles.root}>
      <label htmlFor={name} className={cn(styles.label, labelClassName)}>
        {label}
      </label>
      {editable && (
        <CreatableSelect
          isMulti
          options={options}
          onChange={handleSelectChange}
          onCreateOption={handleCreateOption}
          onMenuOpen={handleMenuOpen}
          onInputChange={handleInputChange}
          value={formattedTags}
          placeholder="Add tags here"
          className={styles.inputWrapper}
          styles={customStyles}
          components={customComponents}
        />
      )}
      <InputFooter
        error={errorMessage}
        notice={noticeMessage}
        showCounter={showCounter}
        currentLength={currentTagCount}
        maxLength={MAX_TAGS}
        tooltipId={tooltipId}
      />
    </div>
  );
};

export default TagsInput;
